// per docs/ui-shape-grafana-authoring.md §3 — the dashboards that exist in
// Grafana right now, so the administrator picks one by its title instead of
// remembering a uid (caiet §6 #7, UC26-01, UC-DASH-11).
//
// Served by GET /api/v1/admin/grafana/dashboards (SYSTEM_ADMIN only), which
// reads Grafana's own search: Grafana stays the single source of truth for what
// a dashboard is called and where it lives.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '../app/api-client';

// The response as the BFF's GrafanaCatalogController serves it.
interface GrafanaDashboardSummary {
  uid: string;
  title: string;
  folderUid?: string;
  folderTitle?: string;
  tags?: string[];
  url?: string;
}

export interface GrafanaDashboardRef {
  uid: string;
  title: string;
  folder: string;
  tags: string[];
}

// Grafana's own name for a dashboard saved outside any folder.
const UNFILED_FOLDER = 'General';

export function useGrafanaDashboards(): UseQueryResult<GrafanaDashboardRef[]> {
  return useQuery({
    queryKey: ['grafana-catalogue'],
    queryFn: () =>
      apiClient
        .get<{ dashboards: GrafanaDashboardSummary[] }>('/admin/grafana/dashboards')
        .then((r) =>
          (r.data.dashboards ?? [])
            .filter((row) => Boolean(row.uid))
            .map((row) => ({
              uid: row.uid,
              title: row.title || row.uid,
              folder: row.folderTitle || UNFILED_FOLDER,
              tags: Array.isArray(row.tags) ? row.tags : [],
            })),
        ),
    // The administrator leaves for Grafana, authors, and comes back: a stale
    // list would hide exactly the dashboard they just drew. Coming back focuses
    // the SIMSM tab, so that is the moment to re-read, against the app-wide
    // default of never refetching on focus.
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

function normalize(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

// The administrator recognises what they drew by its title and the folder they
// saved it in; the uid is searchable too so a definition imported from another
// environment can still be found by it.
export function matchesGrafanaDashboard(row: GrafanaDashboardRef, query: string): boolean {
  const trimmed = query.trim();
  if (!trimmed) return true;
  const haystack = normalize([row.title, row.folder, row.uid, ...row.tags].join(' '));
  return normalize(trimmed)
    .split(/\s+/)
    .every((term) => haystack.includes(term));
}
